<template>
    <div class="create-chatbot-wrapper">
        <WebsiteChatbotBuilder v-if="websiteBuilder" @cancel="() => (websiteBuilder = false)" />
        <div class="create-chatbot" v-else>
            <div class="sticky-header">
                <div class="title">{{$t("chatbotv2.setUpYourChatbot")}}</div>
                <div class="description">
                    {{$t("chatbotv2.setUpYourChatbotDesc")}}
                </div>
                <SupsisButton class="btnCloseCb" small @click="close">
                    <i class="material-icons">close</i>
                </SupsisButton>
            </div>

            <div class="items">
                <div class="block block-scratch" @click="createBlankStory">
                    <div class="icon">
                        <i class="material-icons">add</i>
                    </div>
                    <div class="name">{{$t("chatbotv2.blankBot")}}</div>
                    <div class="description">{{$t("chatbotv2.blankBotDesc")}}</div>
                </div>
                <div class="block block-ai" @click="channelNotAvailable">
                    <div class="ai-badge">
                        <span class="lang">ENGLISH/TURKISH</span>
                        <span>AI</span>
                    </div>
                    <div class="icon">
                        <i class="material-icons">public</i>
                    </div>
                    <div class="name">Website</div>
                    <div class="description">
                        {{$t("chatbotv2.websiteDesc")}}
                    </div>
                </div>
                <div class="divider-items">
                    <div class="half-template-area">
                        <a-divider class="template-divider">{{ $t("chatbotv2.readyTemplates") }}</a-divider>
                        <div class="half-template-items first">
                            <div
                                class="block block-ai block-template"
                                v-for="template in getFilteredTemplates()"
                                @click="selectedTemplate = template"
                            >
                                <div class="ai-badge">
                                    <span>{{ $t("chatbotv2.basicTemplate") }}</span>
                                </div>
                                <div class="icon">
                                    <img :src="template.imageUrl" alt="" />
                                </div>
                                <div class="name">{{ template.name[currentLang] }}</div>
                                <div class="description">{{ template.shortDescription[currentLang] }}</div>
                            </div>
                        </div>
                    </div>
                    <div class="half-template-area">
                        <a-divider class="template-divider">{{ $t("chatbotv2.templatesFromTheIndustry") }}</a-divider>

                        <div class="half-template-items">
                            <div
                                class="block block-ai block-template"
                                v-for="template in getFilteredAiTemplates()"
                                @click="selectedTemplate = template"
                            >
                                <div class="ai-badge">
                                    <span>{{ $t("chatbotv2.proTemplate") }}</span>
                                </div>
                                <div class="icon">
                                    <img :src="template.imageUrl" alt="" />
                                </div>
                                <div class="name">{{ template.name[currentLang] }}</div>
                                <div class="description">{{ template.shortDescription[currentLang] }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Modal2
            id="openAI"
            v-if="openAIModal"
            :showTitle="false"
            :showFooter="false"
            @cancel="openAIModal = false"
            class="open-ai-wrapper"
        >
            <div class="open-ai-modal-body">
                <ApiKeyIcon style="width: 400px; height: 250px" />
                <p class="open-ai-modal-text">
                    {{$t("chatbotv2.yourNeedEnterYourOpenAi")}}
                </p>
                <div class="delete-modal-btn">
                    <button class="push-btn-give-up" @click="openAIModal = false">{{$t("message.cancel")}}</button>
                    <button class="push-btn" @click="openAIChannelCreate">{{$t("chatbotv2.createOpenAiKey")}}</button>
                </div>
            </div>
        </Modal2>
        <a-modal
            centered
            :visible="!!selectedTemplate"
            :footer="null"
            :closable="true"
            @cancel="selectedTemplate = null"
            v-if="selectedTemplate"
            class="template-modal"
        >
            <div class="modal-import-template">
                <div class="description">
                    <div class="top">
                        <img :src="selectedTemplate.imageUrl" :alt="selectedTemplate.name" class="image" />
                        <div class="header">
                            <div class="title">
                                {{ selectedTemplate.name[currentLang] }}
                            </div>
                            <a-button
                                type="primary"
                                ghost
                                :disbled="importing"
                                :loading="importing"
                                @click="() => createStory(selectedTemplate.slug)"
                                :disabled="!selectedTemplate.enabled"
                            >
                                {{ importing ? $t("chatbotv2.importing") : $t("chatbotv2.importBot") }}
                            </a-button>
                        </div>
                    </div>
                    <div class="tpl-markdown description-content">
                        <p>
                            {{ selectedTemplate.description[currentLang].split("\n")[0] }}
                        </p>
                        <div
                            v-if="
                                selectedTemplate.description[currentLang].split('\n')[3] !== '' &&
                                !isThatAI(selectedTemplate)
                            "
                        >
                            <p>
                                <strong>{{ $t("chatbotv2.keyFeatures") }}:</strong>
                            </p>
                            <ul>
                                <li
                                    v-for="(keyFeature, index) of $_.drop(
                                        selectedTemplate.description[currentLang].split('\n'),
                                        3
                                    )"
                                    :key="index"
                                >
                                    {{ keyFeature.replace("*", "").trim() }}
                                </li>
                            </ul>
                        </div>
                    </div>
                    <!---->
                </div>
                <div class="details">
                    <div class="section">
                        <div class="label">{{ $t("chatbotv2.lastUpdate") }}</div>
                        <div class="data">{{ timeHumanize(selectedTemplate.updatedAt) }}</div>
                    </div>
                    <div class="section">
                        <div class="label">{{ $t("chatbotv2.categories") }}</div>
                        <div class="data" style="flex-direction: column; align-items: flex-start">
                            {{ selectedTemplate.categories[currentLang].join(", ") }}
                        </div>
                    </div>
                    <div class="section">
                        <div v-if="$i18n.locale === 'en' || $i18n.locale === 'ru'" class="label">
                            {{ $t("chatbotv2.worksWith") }}
                        </div>
                        <div class="data" style="flex-direction: column; align-items: flex-start">
                            <div class="display-integrations">
                                <img class="icon supsis" src="/static/img/supsis-logo3.png" />

                                <WhatsappV1 :color="'#28AC01'" class="templates-whatsapp-icon" />
                                <WhatsappV2 :color="'#28AC01'" class="templates-whatsapp-icon" />
                                <WhatsappBusinessIcon :color="'#28AC01'" class="templates-whatsapp-icon" />
                                <TelegramIcon />
                                <InstagramIcon special-background-mode />
                                <MessengerIcon />
                            </div>
                        </div>
                        <div v-if="$i18n.locale === 'tr'" class="label">{{ $t("chatbotv2.worksWith") }}</div>
                    </div>
                </div>
            </div>
        </a-modal>
    </div>
</template>
<script>
import Vue from "vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import WebsiteChatbotBuilder from "./WebsiteChatbotBuilder.vue";
import { mapActions, mapState } from "vuex";
import Modal2 from "../../../../../components/common/Modal2.vue";
import ApiKeyIcon from "../integrations/svg/ApiKeyIcon.vue";
import CHATBOT_TEMPLATES from "../templates/chatbot-templates";
import _ from "lodash";
import CONFIG from "../../../../../config/config";
import TelegramIcon from "../integrations/svg/TelegramIcon.vue";
import moment from "moment/moment";
import CHATBOT_AI_TEMPLATES from "../templates/chatbot-ai-templates";
import WhatsappV2 from "../integrations/svg/specialWhatsappIcon/WhatsappV2.vue";
import WhatsappV1 from "../integrations/svg/specialWhatsappIcon/WhatsappV1.vue";
import WhatsappBusinessIcon from "../integrations/svg/WhatsappBusinessIcon.vue";
import InstagramIcon from "../integrations/svg/InstagramIcon.vue";
import MessengerIcon from "../integrations/svg/MessengerIcon.vue";

export default Vue.extend({
    name: "CreateChatbot",
    components: {
        TelegramIcon,
        WebsiteChatbotBuilder,
        SupsisButton,
        ApiKeyIcon,
        Modal2,
        WhatsappV1,
        WhatsappV2,
        WhatsappBusinessIcon,
        InstagramIcon,
        MessengerIcon,
    },
    data() {
        return {
            websiteBuilder: false,
            openAIModal: false,
            templateItems: CHATBOT_TEMPLATES,
            aiTemplateItems: CHATBOT_AI_TEMPLATES,
            akinonEnabled: CONFIG.AKINON.enabledReal,
            enabledSlugs: [],
            selectedTemplate: null,
            importing: false,
            cancels: [],
            currentLang: "",
        };
    },
    computed: {
        ...mapState({
            openAIChannel: (state) => state.channel.channels.find((x) => x.type === "openai"),
        }),
    },
    methods: {
        ...mapActions({
            createEmptyStory: "CB/createEmptyStory",
            createStoryFromTemplate: "CB/createStoryFromTemplate",
            listStories: "CB/listStories",
        }),
        isThatAI(selectedTemplate) {
            return selectedTemplate.categories.en.includes("artificial-intelligent");
        },

        createStory(slug) {
            this.importing = true;
            this.createStoryFromTemplate(slug)
                .then((result) => {
                    if (!!result) {
                        this.selectedTemplate = null;
                        this.$message.success({
                            content: `Story imported successfully`,
                            duration: 2,
                        });
                        setTimeout(() => {
                            this.$router.push(`/chatbot/story/${result.story._id}/${result.board.version}`);
                        }, 500);
                    } else {
                        this.$message.error({
                            content: `Story import failed`,
                            duration: 2,
                        });
                    }
                })
                .catch((err) => {
                    this.$message.error({
                        content: `Story import failed`,
                        duration: 2,
                    });
                })
                .finally(() => {
                    this.importing = false;
                });
        },
        timeHumanize(date) {
            return moment(date).calendar();
        },
        close() {
            this.$emit("close");
        },
        createBlankStory() {
            const hide = this.$message.loading({
                content: this.$t('chatbotv2.creatingBlank'),
                duration: 20,
            });
            this.createEmptyStory({
                title: this.$t('chatbotv2.untitledChatbot'),
                description: "",
            })
                .then(({ story }) => {
                    this.$router.push(`/chatbot/story/${story._id}/1.0`);
                })
                .catch((err) => {
                    this.$message.error({
                        content: this.$t('chatbotv2.errorCreating'),
                        duration: 3,
                    });
                })
                .finally(() => {
                    hide();
                    this.listStories();
                });
        },
        channelNotAvailable() {
            if (this.openAIChannel) {
                this.websiteBuilder = true;
            } else {
                this.openAIModal = true;
            }
        },
        openAIChannelCreate() {
            this.$router.push(`/integrations/openai`);
        },
        getFilteredTemplates() {
            const q = (this.search || "").trim().toLowerCase();
            return this.templateItems
                .filter((x) => x.visible)
                .filter((item) => {
                    if (_.isEmpty(this.enabledSlugs)) {
                        return _.isEmpty(q) ? true : item.name.trim().toLowerCase().includes(q);
                    } else {
                        return (
                            this.enabledSlugs.includes(item.slug) &&
                            (_.isEmpty(q) ? true : item.name.trim().toLowerCase().includes(q))
                        );
                    }
                });
        },
        getFilteredAiTemplates() {
            const q = (this.search || "").trim().toLowerCase();
            return this.aiTemplateItems
                .filter((x) => x.visible)
                .filter((item) => {
                    if (_.isEmpty(this.enabledSlugs)) {
                        return _.isEmpty(q) ? true : item.name.trim().toLowerCase().includes(q);
                    } else {
                        return (
                            this.enabledSlugs.includes(item.slug) &&
                            (_.isEmpty(q) ? true : item.name.trim().toLowerCase().includes(q))
                        );
                    }
                });
        },
    },
    mounted() {
        this.enabledSlugs = this.akinonEnabled ? ["us-polo-assn", "pierre-cardin", "cacharel"] : [];
        const escapeToEscape = (e) => {
            let val = e.key;
            if (e.key === "Escape") {
                this.close();
            }
        };
        window.addEventListener("keydown", escapeToEscape);

        this.currentLang = this.$i18n.locale === "en" || this.$i18n.locale === "tr" ? this.$i18n.locale : "en";
        this.cancels.push(() => {
            window.removeEventListener("keydown", escapeToEscape);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
});
</script>

<style scoped lang="scss">
.create-chatbot-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    min-height: 100%;
    height: 100%;
    background: white;
    z-index: 1000 !important;
}

.create-chatbot {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 80px;
    position: relative;
    height: 100%;
    overflow-y: auto;
    width: 100%;

    .sticky-header {
        background: #fff;
        max-width: 1600px;
        padding: 40px 0;
        position: sticky;
        top: 0;
        width: 100%;
        z-index: 999 !important;
        display: flex;
        flex-direction: column;
        gap: 10px;

        .title {
            font-size: 30px;
            line-height: 38px;
            color: #22272d;
            font-weight: 700;
        }

        .description {
            font-size: 18px;
            line-height: 23px;
        }

        .btnCloseCb {
            position: absolute;
            right: 0;
        }
    }

    .items {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;

        .divider-items {
            width: 100%;
            display: flex;
            gap: 7px;

            .half-template-area {
                width: 50%;

                .half-template-items {
                    display: flex;
                    width: 100%;
                    flex-wrap: wrap;
                    gap: 5px;
                    /*&.first {
                        border-right: 1px solid rgb(0, 0, 0);
                    }*/

                    .block-template {
                        max-width: 300px !important;
                        height: 200px !important;
                    }
                }
            }
        }

        .block {
            background-color: #fff;
            border: 2px solid #f5f5f5;
            border-radius: 15px;
            cursor: pointer;
            padding: 20px;
            transition: border-color 0.1s;
            width: 400px;
            height: 175px;

            &:hover {
                border-color: #006cff !important;
            }

            .name {
                color: #22272d;
                font-size: 18px;
                font-weight: 600;
                line-height: 23px;
                margin-bottom: 8px;
                margin-top: 13px;
            }

            .description {
                color: #22272d;
                font-size: 16px;
                line-height: 20px;
            }

            &.block-scratch {
                .icon {
                    background: #f5f5f5;
                    border-radius: 100%;
                    display: flex;
                    height: 34px;
                    width: 34px;
                    align-items: center;
                    justify-content: center;
                }
            }

            &.block-ai {
                background: #fff;
                border-color: #f5f5f5;
                position: relative;

                .ai-badge {
                    display: flex;
                    font-size: 12px;
                    font-weight: 700;
                    line-height: 16px;
                    justify-content: center;
                    align-items: center;
                    position: absolute;
                    right: 10px;
                    top: 10px;

                    span {
                        background: #22272d;
                        border-radius: 6px;
                        color: #fff;
                        margin-left: 4px;
                        padding: 0 4px;

                        &.lang {
                            background: #f5f5f5;
                            color: #22272d;
                        }
                    }
                }

                .icon {
                    color: #006cff;
                    height: 34px;
                    margin-bottom: 12px;
                    width: 34px;
                }
            }
        }
    }
}

.open-ai-modal-body {
    display: block;
    justify-content: center;
    align-items: center;
    height: 100%;

    .open-ai-modal-text {
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 16px;
        font-weight: 500;
        color: #181c32;
        margin-bottom: 20px;
        text-align: center;
    }

    .delete-modal-btn {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 50px;

        .push-btn {
            background: #ff7900;
            border: none;
            outline: none;
            color: #ffffff;
            font-size: 14px;
            font-weight: 500;
            padding: 10px 20px;
            border-radius: 4px;
        }

        .push-btn-give-up {
            background: transparent;
            border: 1px solid #ff7900;
            outline: none;
            color: #ff7900;
            font-size: 14px;
            font-weight: 500;
            padding: 10px 20px;
            border-radius: 4px;
        }
    }
}
</style>
<style lang="scss">
.open-ai-wrapper {
    .ant-modal-wrap {
        z-index: 10001 !important;
    }

    .ant-modal-mask {
        z-index: 10001 !important;
    }
}

.create-chatbot-wrapper {
    .template-modal {
        .ant-modal {
            z-index: 10001 !important;
        }
    }
}

.items {
    .template-divider {
        margin-top: 40px;
        margin-bottom: 40px;
        width: 80%;
        padding: 8px;

        .ant-divider-inner-text {
            font-size: 25px !important;
            font-weight: 700;
        }

        &::before {
            border-top: 1px solid #333333;
        }

        &::after {
            border-top: 1px solid #333333;
        }
    }
}
</style>
